import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { EGuideContext, getDoctorSpecializationLocalization } from '@project/view-models';
import { TranslateService } from '@project/translate';
import {
  GuideDto,
  SessionReferralGuideService,
} from '../../../../../lazy-modules/session-details/session-referral-guide/session-referral-guide.service';
import { IPagingResponse } from '@core/http';
import { SessionDetailsApacService } from '../../../../../lazy-modules/session-details/session-details-apac/session-details-apac.service';
import { SessionDetailsSadtService } from '../../../../../lazy-modules/session-details/session-details-sadt/session-details-sadt.service';

interface ExamItem {
  id: string;
  type: 'APAC' | 'SADT';
  created_at: string;
  doctor_specialization: string;
}

@Component({
  selector: 'app-exams-guide',
  templateUrl: './exams-guide.component.html',
  styleUrls: ['./exams-guide.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExamsGuideComponent implements OnInit, OnDestroy {
  @Input() logId!: string;
  @Input() context!: EGuideContext;

  public isLoading = false;
  public error = false;

  public exams: ExamItem[] = [];
  public currentPage = 1;
  readonly ITEMS_PER_PAGE = 10;
  public totalItems = 0;
  public totalPages = 0;
  public getDoctorSpecializationLocalization = getDoctorSpecializationLocalization;
  private destroy$ = new Subject<void>();

  constructor(
    private sessionReferralGuideService: SessionReferralGuideService,
    private sessionDetailsApacService: SessionDetailsApacService,
    private sessionDetailsSadtService: SessionDetailsSadtService,
    private notificationsService: NotificationsService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadExams(1);
  }

  loadExams(page: number): void {
    this.isLoading = true;
    this.error = false;

    const request$ =
      this.context === EGuideContext.PATIENT
        ? this.sessionReferralGuideService.getGuidesviaPatientId(this.logId, page, this.ITEMS_PER_PAGE)
        : this.sessionReferralGuideService.getGuidesviaRequisitionId(this.logId, page, this.ITEMS_PER_PAGE);

    request$
      .pipe(
        map((res: IPagingResponse<GuideDto>) => {
          this.totalItems = res.totalItemsCount;
          this.totalPages = res.requestTotalPages;
          this.currentPage = page;

          return (res.items ?? [])
            .filter((g) => ['APAC', 'SADT'].includes(g.type?.toUpperCase()))
            .map(
              (item): ExamItem => ({
                id: item.id,
                type: item.type.toUpperCase() as 'APAC' | 'SADT',
                created_at: item.created_at,
                doctor_specialization: item.doctor_specialization ?? '-',
              }),
            );
        }),
        tap((items) => {
          this.exams = items;
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
        catchError(() => {
          this.error = true;
          this.exams = [];
          this.isLoading = false;
          this.notificationsService.error({
            message: TranslateService.localize('errors.exams-referral-guides'),
          });
          this.cdr.markForCheck();
          return of([]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onExamDownloadClick(item: ExamItem): void {
    const download$ =
      item.type === 'APAC'
        ? this.sessionDetailsApacService.downloadApac(item.id)
        : this.sessionDetailsSadtService.downloadSadt(item.id);

    download$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (url) => {
        if (url) {
          window.open(url, '_blank');
        }
      },
      error: () => {
        this.notificationsService.error({
          message: TranslateService.localize('errors.download-failed'),
        });
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
